import * as React from 'react'
import { PageDefinition, PageType } from '../pageDefinition'
import { useNavigate, matchPath, useLocation, useParams } from 'react-router-dom'
import { PageContext } from 'contexts'
import {
  useCustomers,
  useCustomerApi,
  useAllCustomersHealthSync,
  usePageNotifications,
  useLogger,
  useAuth,
  useIsFirstRender,
  useFormFactor,
} from 'hooks'
import {
  PageTitleRightActions,
  SidePanelContent,
  ConnectorHealthBadge,
} from 'components'
import {
  EditCustomer,
  DataSearch,
  DataMigration,
  DatabaseConfiguration,
  QueryDocuments,
  ActivityLog,
} from 'components/customers'
import { Customer } from 'contexts/base'
import {
  Box,
  Typography,
  Button,
  ConfirmationModal,
  Dot,
} from 'cs-kevlar-react'
import {
  FilterOptions,
  PaginationOptions,
  Table,
} from 'cs-kevlar-react/lib/es/table/Table'
import { useFilterSuggestions } from 'cs-kevlar-react/lib/es/table/useFilterSuggestions'
import { EL400 } from 'cs-kevlar-react/lib/es/theming/palette'
import { Spacing } from 'cs-kevlar-react/lib/es/theming/baseConstants'
import { ColumnDef, MenuGroup } from 'cs-kevlar-react/lib/es/table/base'
import {
  CustomerProductLine,
  ConnectorType,
  SupportUserPermission,
  WebAdminUser,
} from '../../api'
import {
  getCustomerProductLineLabel,
  getConnectorTypeLabel,
  matchIdOrDefault,
  considerDataToBeStale,
  DEFAULT_SUPPORT_ROUTE,
  serverDateToLocaleDateString,
  serverDateToIsoDateString,
  hasOneOfPermissions,
} from 'utils'
import { buildNavLink as buildCustomerDetailsNavLink } from './CustomerDetails'
import {
  makeStyles,
  createStyles,
  Theme,
} from 'cs-kevlar-react/lib/es/theming/theme'
import qs from 'qs'
import { IconButton } from 'cs-kevlar-react'
import { AddCircle } from 'cs-kevlar-react/lib/es/icons'
import { usePageViewAudit } from 'hooks/usePageViewAudit'

const PageTitle = 'Customer Management'

type CustomersProps = {
  customerAgencyId: string
  licensedAgencyId: string
  customerId?: string
}

type EditingCustomerState = Customer | null

type ValidMenuKey =
  | 'Details'
  | 'Edit'
  | 'MigrateData'
  | 'RefreshData'
  | 'Search'
  | 'QueryDocuments'
  | 'DatabaseConfiguration'
  | 'ActivityLog'

const FILTER_OPTIONS: Partial<FilterOptions> = {
  filterable: true,
  disableLiveFiltering: true,
  enableMultipleSearchTerms: true,
  enableSuggestions: true,
  enableClearAll: true,
}

const PAGINATION_OPTIONS: Partial<PaginationOptions> = {
  singularAdjective: 'customer',
  pluralAdjective: 'customers',
}

const canViewDocuments = (user: WebAdminUser | null) => {
  return (
    user !== null &&
    user.isSupport &&
    user.permissions &&
    user.permissions.includes(SupportUserPermission.ViewDocuments)
  )
}

const useChipStyles = makeStyles((theme: Theme) =>
  createStyles({
    chip: {
      marginLeft: theme.spacing(8),
    },
  }),
)

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    queryDocuments: {
      '& .MuiDialog-paperWidthSm': {
        maxWidth: 750,
        width: 750,
      },
    },
    dataSearch: {
      '& .MuiDialog-paperWidthSm': {
        maxWidth: 750,
        width: 750,
      },
    },
    iconButton: {
      // since this is hosted in the page title toolbar which has a right gutter
      // and the icon button itself has the same right padding, do a negative margin
      // so the button padding is used instead of the margin
      marginRight: -1 * parseInt(theme.spacing(8)),
      '& .MuiSvgIcon-root': {
        fontSize: '1.5rem',
      },
    },
  }),
)

const BASE_PATHNAME = '/customers'

const defaultCustomer: Customer = {
  id: 'NEW',
  customerAgencyId: 'NEW',
  name: '',
  notes: '',
  agencies: [],
  licensedAgencies: [],
  productLine: CustomerProductLine.None,
  connectorType: ConnectorType.None,
  hasOverriddenConnectorConfigurations: false,
  createdBySupportUserName: '',
  updatedBySupportUserName: '',
}

const buildNavigationUrl = (customer: EditingCustomerState) => {
  if (customer === null) return BASE_PATHNAME

  return `${BASE_PATHNAME}/${customer.customerAgencyId}`
}

const EditingCustomerPath = `${BASE_PATHNAME}/:customerAgencyId`
const DataSearchPath = `${BASE_PATHNAME}/search/:customerAgencyId`
const DataMigrationPath = `${BASE_PATHNAME}/migrate/:customerAgencyId`
const DatabaseConfigurationPath = `${BASE_PATHNAME}/database/:customerAgencyId`
const QueryDocumentsPath = `${BASE_PATHNAME}/:customerAgencyId/documents`
const ActivityLogPath = `${BASE_PATHNAME}/:customerAgencyId/audit`

const Customers: React.FunctionComponent = () => {

  const params = useParams<CustomersProps>()
  if(!params) return null

  usePageViewAudit(PageTitle, true, true)

  const navigate = useNavigate()
  const location = useLocation()
  const { logError } = useLogger()
  const formFactor = useFormFactor()

  const classes = useStyles({})

  const isFirstRender = useIsFirstRender()

  const { user: authUser } = useAuth()
  const { state: pageState, dispatch: dispatchPageAction } = React.useContext(
    PageContext,
  )

  const {
    customers,
    lastApiReceiveAt,
    customersContext,
    dispatch: dispatchCustomerAction,
  } = useCustomers()

  useAllCustomersHealthSync()

  // on first render, we will compare the search terms from both the query string as well as context
  // if the query string is populated than we will use the query string
  // if the query string is empty but the context is not then we will use the context
  const queryParams = qs.parse(location.search, { ignoreQueryPrefix: true })
  const querySearchTerms = queryParams.searchTerms
    ? (queryParams.searchTerms as string[])
    : []
  const stateSearchTerms =
    pageState.persistedState?.[BASE_PATHNAME]?.searchTerms ?? []
  const defaultedSearchTerms =
    querySearchTerms.length > 0 ? querySearchTerms : (stateSearchTerms as string[])
  const [searchTerms, setSearchTerms] = React.useState<string[]>(
    defaultedSearchTerms,
  )

  const updateQueryStringWithSearchTerms = (
    newSearchTerms: string[],
  ) => {
    const queryParams = qs.parse(location.search, { ignoreQueryPrefix: true })
    if (newSearchTerms.length > 0) {
      queryParams.searchTerms = newSearchTerms
    } else if (queryParams.searchTerms && newSearchTerms.length === 0) {
      delete queryParams.searchTerms
    }

    const newSearch = `?${qs.stringify(queryParams, { arrayFormat: 'indices' })}`
    if (location.search === newSearch) return

    navigate({
      ...location,
      search: newSearch,
    })
  }

  const onSearchTermsChanged = React.useMemo(() => {
    return (newSearchTerms: string[]) => {
      // update the querystring based filters.  a separate side effect will save those to state and context
      updateQueryStringWithSearchTerms(newSearchTerms)
    }
  }, [location])

  const { getCustomers, refreshData } = useCustomerApi()

  const { dispatch: dispatchNotification } = usePageNotifications()

  const selectCustomerForEditing = (customer: Customer | null) => {
    navigate(buildNavigationUrl(customer))
  }

  const getFilterSuggestions = useFilterSuggestions<Customer, ValidMenuKey>()

  // load the customer data
  React.useEffect(() => {
    // when we load the page, check to see if we have filters sourced from the persisted context
    // that need to be set in the querystring
    if (searchTerms.length > 0) {
      updateQueryStringWithSearchTerms(searchTerms)
    }

    // if the data is new enough then don't bother refetching
    if (!considerDataToBeStale(customers, lastApiReceiveAt)) return

    dispatchCustomerAction({
      type: 'SetLoadingState',
      payload: 'Loading',
    })

    getCustomers()
      .then((response) => {
        if (!response) return

        if (response.status === 200) {
          const customers = response.data.map((customer) => {
            return {
              ...customer,
              id: customer.customerAgencyId,
            }
          })

          dispatchCustomerAction({
            type: 'LoadCustomers',
            payload: customers,
            timestamp: Date.now(),
          })
        } else {
          dispatchNotification({
            type: 'SetError',
            payload: 'There was an error loading the customer list',
          })
          dispatchCustomerAction({
            type: 'SetLoadingState',
            payload: 'None',
          })
        }
      })
      .catch((error) => {
        logError(error)

        dispatchNotification({
          type: 'SetError',
          payload: 'There was an error loading the customer list',
        })
        dispatchCustomerAction({
          type: 'SetLoadingState',
          payload: 'None',
        })
      })
  }, [])

  // anytime the search terms in the url change then save them to state and context
  React.useEffect(() => {
    // if we are in the first render then skip this since the mount side effect updates the URL
    if (isFirstRender) return

    const queryParams = qs.parse(location.search, { ignoreQueryPrefix: true })
    const querySearchTerms = queryParams.searchTerms
      ? (queryParams.searchTerms as string[])
      : []

    setSearchTerms(querySearchTerms ?? [])
  }, [location.search])

  // persist any search term changes from local state to the context
  React.useEffect(() => {
    dispatchPageAction({
      type: 'SetPersistedPageStateBag',
      payload: {
        pagePathname: BASE_PATHNAME,
        state: {
          searchTerms: searchTerms,
        },
      },
    })
  }, [searchTerms])

  const performRefresh = (id: string) => {
    refreshData(id)
      .then((response) => {
        if (!response) return

        if (response.status === 200) {
          dispatchNotification({
            type: 'SetSuccess',
            payload: 'Customer data refresh initiated',
          })
        } else {
          dispatchNotification({
            type: 'SetError',
            payload: 'There was an error refreshing the data',
          })
        }
      })
      .catch((error) => {
        logError(error)

        dispatchNotification({
          type: 'SetError',
          payload: 'There was an error refreshing the data',
        })
      })
  }

  const handleMenuClick = React.useMemo(
    () => (key: ValidMenuKey, row: Customer) => {
      if (key === 'Edit') {
        selectCustomerForEditing(row)
        return
      }

      if (key === 'Details') {
        navigate(buildCustomerDetailsNavLink(row.id))
        return
      }

      if (key === 'MigrateData') {
        navigate(`${BASE_PATHNAME}/migrate/${row.id}`)
        return
      }

      if (key === 'RefreshData') {
        performRefresh(row.id)
        return
      }

      if (key === 'Search') {
        navigate(`${BASE_PATHNAME}/search/${row.id}`)
        return
      }

      if (key === 'DatabaseConfiguration') {
        navigate(`${BASE_PATHNAME}/database/${row.id}`)
        return
      }

      if (key === 'QueryDocuments') {
        navigate(`${BASE_PATHNAME}/${row.id}/documents`)
      }

      if (key === 'ActivityLog') {
        navigate(`${BASE_PATHNAME}/${row.id}/audit`)
      }
    },
    [history],
  )

  const dataSearchMatch = matchPath({
    path: DataSearchPath,
    caseSensitive: true,
  }, location.pathname)
  const isPerformingDataSearch = dataSearchMatch !== null

  const queryDocumentsMatch = matchPath({
    path: QueryDocumentsPath,
    caseSensitive: true,
  }, location.pathname)
  const isPerformingDocumentQuery = queryDocumentsMatch !== null

  const dataMigrationMatch = matchPath({
    path: DataMigrationPath,
    caseSensitive: true,
  }, location.pathname)
  const isMigratingCustomer = dataMigrationMatch !== null

  const configurationDatabaseMatch = matchPath({
    path: DatabaseConfigurationPath,
    caseSensitive: true,
  }, location.pathname)
  const isConfiguringDatabase = configurationDatabaseMatch !== null

  const activityLogsMatch = matchPath({
    path: ActivityLogPath,
    caseSensitive: true,
  }, location.pathname)
  const isViewingActivityLogs = activityLogsMatch !== null

  const editingCustomerMatch = matchPath({
    path: EditingCustomerPath,
    caseSensitive: true,
  }, location.pathname)
  
  const editingCustomer = matchIdOrDefault(
    customersContext.customers,
    params.customerAgencyId,
    defaultCustomer,
  )

  const isEditing =
    editingCustomerMatch !== null &&
    editingCustomer !== null &&
    editingCustomer.customerAgencyId !== 'NEW'
  const isAdding =
    editingCustomerMatch !== null &&
    editingCustomer !== null &&
    editingCustomer.customerAgencyId === 'NEW'

  const columns: ColumnDef<Customer, ValidMenuKey>[] = React.useMemo(() => {
    const menuGroups: MenuGroup<Customer, ValidMenuKey>[] = [
      {
        options: [
          {
            label: 'Details',
            key: 'Details',
          },
        ],
      },
      {
        options: [
          {
            label: 'Edit',
            key: 'Edit',
          },
          {
            label: 'Search',
            key: 'Search',
          },
          {
            label: 'Refresh Data',
            key: 'RefreshData',
          },
          {
            label: 'Migrate Data',
            key: 'MigrateData',
          },
          {
            label: 'Query Documents',
            key: 'QueryDocuments',
            visible: (row: Customer) => canViewDocuments(authUser),
          },
          {
            label: 'Activity Log',
            key: 'ActivityLog',
          },
        ],
      },
    ]

    if (
      hasOneOfPermissions(authUser, [
        SupportUserPermission.ViewDatabaseConfiguration,
        SupportUserPermission.ManageDatabaseConfiguration,
      ])
    ) {
      menuGroups[1].options.push({
        label: 'Database Configuration',
        key: 'DatabaseConfiguration',
      })
    }

    return [
      {
        label: '',
        sortable: false,
        sortByDefault: false,
        filterable: true,
        // use typeof undefined here since Black is 0 which is falsy
        filterFunc: (row: Customer) =>
          typeof row.minimumConnectorHealth !== 'undefined'
            ? ConnectorHealthBadge.TooltipLabels[row.minimumConnectorHealth]
            : null,
        align: 'right',
        // 16px plus 4 for the right aligned badge which is half in and half out
        width: Spacing * 10,
        padding: 'none',
        component: ({ row }) => (
          <ConnectorHealthBadge
            health={row.minimumConnectorHealth}
            enableUnknownStatus
          />
        ),
      },
      {
        label: 'Customer',
        sortable: true,
        sortByDefault: true,
        sortValueFunc: (row: Customer) => row.name,
        filterable: true,
        component: ({ row }) => {
          const classes = useChipStyles({})
          return (
            <Box>
              <Typography display="inline">{row.name}</Typography>
              {row.hasOverriddenConnectorConfigurations && (
                <Dot
                  className={classes.chip}
                  enableTooltip
                  label="Config Overridden"
                  color={EL400}
                />
              )}
            </Box>
          )
        },
        dataProperty: 'name' as keyof Customer,
      },
      {
        label: 'Agencies',
        sortable: true,
        sortByDefault: false,
        sortValueFunc: (row: Customer) => (row.licensedAgencies ?? []).length,
        filterable: true,
        dataFunc: (row: Customer) => (row.licensedAgencies ?? []).length,
      },
      {
        label: 'Product Line',
        sortable: true,
        sortByDefault: false,
        sortValueFunc: (row: Customer) =>
          getCustomerProductLineLabel(row.productLine),
        filterable: true,
        dataFunc: (row: Customer) =>
          getCustomerProductLineLabel(row.productLine),
      },
      {
        label: 'Connector Type',
        sortable: true,
        sortByDefault: false,
        sortValueFunc: (row: Customer) =>
          getConnectorTypeLabel(row.connectorType),
        filterable: true,
        dataFunc: (row: Customer) => getConnectorTypeLabel(row.connectorType),
      },
      {
        label: 'Most Recent Connector Added',
        sortable: true,
        sortByDefault: false,
        // sort with the ISO date string since that results in an expected sort order
        sortValueFunc: (row: Customer) =>
          serverDateToIsoDateString(row.mostRecentInterfaceCreatedOn),
        filterable: true,
        dataFunc: (row: Customer) =>
          serverDateToLocaleDateString(row.mostRecentInterfaceCreatedOn),
      },
      {
        label: '',
        sortable: false,
        sortByDefault: false,
        filterable: false,
        menuGroups,
      },
    ]
  }, [authUser])

  return (
    <React.Fragment>
      <PageTitleRightActions>
        {!formFactor.isPhone && (
          <Button
            onClick={() => selectCustomerForEditing(defaultCustomer)}
            color="primary"
          >
            Add New Customer
          </Button>
        )}
        {formFactor.isPhone && (
          <IconButton
            onClick={() => selectCustomerForEditing(defaultCustomer)}
            color="primary"
            className={classes.iconButton}
            size="large">
            <AddCircle />
          </IconButton>
        )}
      </PageTitleRightActions>
      <Table
        data={customers}
        filterOptions={FILTER_OPTIONS}
        paginationOptions={PAGINATION_OPTIONS}
        columns={columns as ColumnDef<Customer, ValidMenuKey>[]}
        ariaLabel="Customers"
        useDefaultRow
        handleMenuClick={handleMenuClick}
        isLoading={customersContext.loadingState.status === 'Loading'}
        selectedId={isEditing ? editingCustomer?.customerAgencyId : undefined}
        uniqueIdentifier="CustomersList"
        searchTerms={searchTerms}
        onSearchChanged={onSearchTermsChanged}
        getFilterSuggestions={getFilterSuggestions}
      />
      {(isEditing || isAdding) && (
        <SidePanelContent
          anchor="right"
          title={isAdding ? 'Add Customer' : `Edit Customer`}
          onClose={() => selectCustomerForEditing(null)}
        >
          <EditCustomer
            key={(editingCustomer as Customer).id}
            customerAgencyId={(editingCustomer as Customer).id}
            customer={editingCustomer as Customer}
            onClose={() => selectCustomerForEditing(null)}
          />
        </SidePanelContent>
      )}
      {isMigratingCustomer && (
        <SidePanelContent
          anchor="right"
          title="Customer Data Migration"
          onClose={() => navigate(DEFAULT_SUPPORT_ROUTE)}
        >
          <DataMigration
            key={params.customerAgencyId as string}
            customerAgencyId={params.customerAgencyId as string}
            onClose={() => navigate(DEFAULT_SUPPORT_ROUTE)}
          />
        </SidePanelContent>
      )}
      {isConfiguringDatabase && (
        <SidePanelContent
          anchor="right"
          title="Customer Database Configuration"
          onClose={() => navigate(DEFAULT_SUPPORT_ROUTE)}
        >
          <DatabaseConfiguration
            key={params.customerAgencyId as string}
            customerAgencyId={params.customerAgencyId as string}
            onClose={() => navigate(DEFAULT_SUPPORT_ROUTE)}
          />
        </SidePanelContent>
      )}
      {isViewingActivityLogs && (
        <SidePanelContent
          anchor="right"
          title="Activity Log"
          auditTitle={`${PageTitle} / ${editingCustomer?.name} / Activity Log`}
          isReadyToAudit={!!editingCustomer}
          isSupportPage={true}
          onClose={() => navigate(DEFAULT_SUPPORT_ROUTE)}
        >
          <ActivityLog
            key={params.customerAgencyId as string}
            customerAgencyId={params.customerAgencyId as string}
            onClose={() => navigate(DEFAULT_SUPPORT_ROUTE)}
          />
        </SidePanelContent>
      )}
      <ConfirmationModal
        onCancel={() => navigate(DEFAULT_SUPPORT_ROUTE)}
        open={isPerformingDataSearch}
        title="Data Search"
        hideActions
        className={classes.dataSearch}
        maxWidth="lg"
        fullWidth
        message={
          <DataSearch
            key={params.customerAgencyId as string}
            customerAgencyId={params.customerAgencyId as string}
          />
        }
      />
      <ConfirmationModal
        onCancel={() => navigate(-1)}
        open={isPerformingDocumentQuery}
        title="Query Documents For"
        hideActions
        className={classes.queryDocuments}
        message={
          <QueryDocuments
            key={queryDocumentsMatch?.params.customerAgencyId as string}
            customerAgencyId={
              queryDocumentsMatch?.params.customerAgencyId as string
            }
          />
        }
      />
    </React.Fragment>
  );
}

export const CustomersPage: PageDefinition = {
  routes: () => [
    "/customers",
    EditingCustomerPath,
    DataSearchPath,
    DataMigrationPath,
    DatabaseConfigurationPath,
    QueryDocumentsPath,
    ActivityLogPath,
  ],
  type: [PageType.Support],
  sortOrder: () => 0,
  component: <Customers />,
  navLinkText: 'Customers',
  pageTitle: PageTitle,
  canAccess: (user) => {
    return user !== null && user.isSupport
  },
  buildNavLink: (user) => {
    return buildNavigationUrl(null)
  },
}
